import React, { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  Users, Package, TrendingUp, AlertTriangle, CheckCircle, XCircle,
  Eye, Trash2, Shield, Settings, BarChart3, Star, Flag, RefreshCw,
  Megaphone, Plus, PenLine, ToggleLeft, ToggleRight,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { listingService } from '../services/listingService';
import { userService } from '../services/userService';
import { adService } from '../services/adService';
import { uploadMedia } from '../services/uploadService';
import { siteSettingsService } from '../services/siteSettingsService';
import { formatPrice, formatDate, getRoleBadgeInfo } from '../utils/formatters';

const TABS = [
  { id: 'dashboard', labelKey: 'admin.tab_dashboard', icon: BarChart3  },
  { id: 'listings',  labelKey: 'admin.tab_listings',  icon: Package    },
  { id: 'users',     labelKey: 'admin.tab_users',     icon: Users      },
  { id: 'reports',   labelKey: 'admin.tab_reports',   icon: Flag       },
  { id: 'ads',       labelKey: 'admin.tab_ads',       icon: Megaphone  },
  { id: 'settings',  labelKey: 'admin.tab_settings',  icon: Settings   },
];

const EMPTY_AD = {
  title: '',
  description: '',
  linkUrl: '',
  mediaUrl: '',
  ctaText: '',
  ctaColor: '#0a84ff',
  bgColor: '#f5f5f7',
  position: 'both',
  isActive: true,
};

function ImageUpload({ value, onChange }) {
  const { t } = useLanguage();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const url = await uploadMedia(file);
      onChange(url);
    } catch {
      setError(t('admin.ad_upload_error') || 'Yükleme başarısız.');
    } finally {
      setUploading(false);
    }
  }

  function handleRemove() {
    onChange('');
  }

  return (
    <div>
      <label className="text-xs font-medium text-apple-gray-mid block mb-1">{t('admin.ad_image')}</label>
      {value ? (
        <div className="relative inline-block">
          <img src={value} alt={t('admin.ad_image')} className="h-24 rounded-xl object-cover border border-gray-200" />
          <button
            type="button"
            onClick={handleRemove}
            className="absolute -top-2 -right-2 w-5 h-5 bg-apple-red text-white rounded-full text-xs flex items-center justify-center leading-none"
          >
            ×
          </button>
        </div>
      ) : (
        <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-apple-blue transition-colors">
          {uploading ? (
            <span className="text-xs text-apple-blue">{t('admin.uploading') || 'Yükleniyor…'}</span>
          ) : (
            <>
              <span className="text-xs text-apple-gray-mid">{t('admin.ad_file_select')}</span>
              <span className="text-[10px] text-apple-gray-mid mt-1">{t('admin.ad_file_hint')}</span>
            </>
          )}
          <input type="file" accept="image/*,.gif,.webp,.glb,.usdz" className="hidden" onChange={handleFile} disabled={uploading} />
        </label>
      )}
      {error && <p className="text-xs text-apple-red mt-1">{error}</p>}
    </div>
  );
}

function AdForm({ initial = EMPTY_AD, onSave, onCancel }) {
  const { t } = useLanguage();
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div className="bg-white rounded-2xl shadow-apple p-5 space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-medium text-apple-gray-mid block mb-1">{t('admin.ad_title')}</label>
          <input
            value={form.title}
            onChange={e => set('title', e.target.value)}
            placeholder={t('admin.ad_title_placeholder')}
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-apple-blue"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-apple-gray-mid block mb-1">Link URL</label>
          <input
            value={form.linkUrl}
            onChange={e => set('linkUrl', e.target.value)}
            placeholder="https://..."
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-apple-blue"
          />
        </div>
      </div>

      <div>
        <label className="text-xs font-medium text-apple-gray-mid block mb-1">{t('admin.ad_description')}</label>
        <textarea
          value={form.description}
          onChange={e => set('description', e.target.value)}
          placeholder={t('admin.ad_description_placeholder')}
          rows={2}
          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-apple-blue resize-none"
        />
      </div>

      <ImageUpload value={form.mediaUrl} onChange={v => set('mediaUrl', v)} />

      <div className="grid sm:grid-cols-3 gap-4">
        <div>
          <label className="text-xs font-medium text-apple-gray-mid block mb-1">{t('admin.ad_cta_text')}</label>
          <input
            value={form.ctaText}
            onChange={e => set('ctaText', e.target.value)}
            placeholder={t('admin.ad_cta_text_placeholder')}
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-apple-blue"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-apple-gray-mid block mb-1">{t('admin.ad_cta_color')}</label>
          <input
            type="color"
            value={form.ctaColor}
            onChange={e => set('ctaColor', e.target.value)}
            className="w-full h-9 border border-gray-200 rounded-xl px-1 py-1 cursor-pointer"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-apple-gray-mid block mb-1">{t('admin.ad_bg_color')}</label>
          <input
            type="color"
            value={form.bgColor}
            onChange={e => set('bgColor', e.target.value)}
            className="w-full h-9 border border-gray-200 rounded-xl px-1 py-1 cursor-pointer"
          />
        </div>
      </div>

      <div className="flex items-center gap-6">
        <div>
          <label className="text-xs font-medium text-apple-gray-mid block mb-1">{t('admin.ad_position')}</label>
          <select
            value={form.position}
            onChange={e => set('position', e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-apple-blue"
          >
            <option value="top">{t('admin.ad_pos_top')}</option>
            <option value="both">{t('admin.ad_pos_both')}</option>
            <option value="left">{t('admin.ad_pos_left')}</option>
            <option value="right">{t('admin.ad_pos_right')}</option>
            <option value="both-full">{t('admin.ad_pos_both_full')}</option>
            <option value="left-full">{t('admin.ad_pos_left_full')}</option>
            <option value="right-full">{t('admin.ad_pos_right_full')}</option>
            <option value="bottom">{t('admin.ad_pos_bottom')}</option>
          </select>
        </div>
        <label className="flex items-center gap-2 cursor-pointer mt-4">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={e => set('isActive', e.target.checked)}
            className="w-4 h-4 rounded"
          />
          <span className="text-sm text-apple-gray-dark font-medium">{t('admin.ad_active')}</span>
        </label>
      </div>

      <div className="flex gap-2 pt-2">
        <button
          onClick={() => onSave(form)}
          className="btn-primary text-sm px-4 py-2"
        >
          {t('admin.save')}
        </button>
        <button
          onClick={onCancel}
          className="btn-secondary text-sm px-4 py-2"
        >
          {t('admin.cancel')}
        </button>
      </div>
    </div>
  );
}

function getPositionLabels(t) {
  return {
    top: t('admin.ad_pos_top_short'),
    both: t('admin.ad_pos_both_short'),
    left: t('admin.ad_pos_left_short'),
    right: t('admin.ad_pos_right_short'),
    'both-full': t('admin.ad_pos_both_full_short'),
    'left-full': t('admin.ad_pos_left_full_short'),
    'right-full': t('admin.ad_pos_right_full_short'),
    bottom: t('admin.ad_pos_bottom_short'),
  };
}

function AdsTab() {
  const { t } = useLanguage();
  const [ads, setAds]           = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => { fetchAds(); }, []);

  async function fetchAds() {
    setLoading(true);
    try {
      const data = await adService.getAll();
      setAds(Array.isArray(data) ? data : []);
    } catch { setAds([]); }
    finally { setLoading(false); }
  }

  async function handleAdd(form) {
    try {
      await adService.create(form);
      setShowForm(false);
      fetchAds();
    } catch { alert('Reklam eklenemedi.'); }
  }

  async function handleEdit(form) {
    try {
      await adService.update(editingId, { ...form, id: editingId });
      setEditingId(null);
      fetchAds();
    } catch { alert('Güncelleme başarısız.'); }
  }

  async function handleToggle(id) {
    try {
      await adService.toggleActive(id);
      fetchAds();
    } catch { alert('İşlem başarısız.'); }
  }

  async function handleDelete(id) {
    if (!window.confirm(t('admin.confirm_delete_ad'))) return;
    try {
      await adService.delete(id);
      fetchAds();
    } catch { alert('Silme başarısız.'); }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-apple-gray-dark">
          {t('admin.ad_management')}
          {!loading && <span className="ml-2 text-xs font-normal text-apple-gray-mid">({ads.length})</span>}
        </h3>
        <div className="flex items-center gap-2">
          <button onClick={fetchAds} className="text-xs text-apple-blue hover:underline flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> {t('admin.refresh')}
          </button>
          {!showForm && !editingId && (
            <button
              onClick={() => setShowForm(true)}
              className="btn-primary text-sm px-3 py-1.5 flex items-center gap-1"
            >
              <Plus className="w-3.5 h-3.5" /> {t('admin.new_ad')}
            </button>
          )}
        </div>
      </div>

      {showForm && (
        <AdForm onSave={handleAdd} onCancel={() => setShowForm(false)} />
      )}

      {loading && (
        <div className="p-8 text-center text-apple-gray-mid text-sm animate-pulse">{t('admin.loading')}</div>
      )}

      {!loading && ads.length === 0 && !showForm && (
        <div className="bg-white rounded-2xl shadow-apple p-10 text-center">
          <Megaphone className="w-10 h-10 text-apple-gray-mid mx-auto mb-3 opacity-40" />
          <p className="text-sm text-apple-gray-mid">{t('admin.no_ads')}</p>
        </div>
      )}

      {ads.map(ad => (
        editingId === ad.id ? (
          <AdForm
            key={ad.id}
            initial={ad}
            onSave={handleEdit}
            onCancel={() => setEditingId(null)}
          />
        ) : (
          <div key={ad.id} className="bg-white rounded-2xl shadow-apple p-4 flex items-start gap-4">
            {ad.mediaUrl && (
              <img src={ad.mediaUrl} alt="" className="w-16 h-16 rounded-xl object-cover shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ad.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {ad.isActive ? t('admin.status_active') : t('admin.status_passive')}
                </span>
                <span className="text-xs text-apple-gray-mid bg-apple-gray px-2 py-0.5 rounded-full">
                  {getPositionLabels(t)[ad.position] || ad.position}
                </span>
              </div>
              <p className="text-sm font-semibold text-apple-gray-dark truncate">
                {ad.title || <em className="text-apple-gray-mid font-normal">{t('admin.ad_untitled')}</em>}
              </p>
              {ad.description && <p className="text-xs text-apple-gray-mid mt-0.5 line-clamp-1">{ad.description}</p>}
              {ad.linkUrl && <p className="text-xs text-apple-blue mt-0.5 truncate">{ad.linkUrl}</p>}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={() => handleToggle(ad.id)}
                className="p-1.5 rounded-lg hover:bg-apple-gray transition-colors"
                title={ad.isActive ? t('admin.deactivate') : t('admin.activate')}
              >
                {ad.isActive
                  ? <ToggleRight className="w-4 h-4 text-apple-green" />
                  : <ToggleLeft className="w-4 h-4 text-apple-gray-mid" />}
              </button>
              <button
                onClick={() => { setEditingId(ad.id); setShowForm(false); }}
                className="p-1.5 rounded-lg hover:bg-apple-gray transition-colors text-apple-gray-mid"
                title={t('admin.edit')}
              >
                <PenLine className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => handleDelete(ad.id)}
                className="p-1.5 rounded-lg hover:bg-red-50 transition-colors text-apple-gray-mid hover:text-apple-red"
                title="Sil"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )
      ))}
    </div>
  );
}

// ── Settings Tab ──────────────────────────────────────────────────────────
function SettingsTab() {
  const { t } = useLanguage();
  const [durationDays, setDurationDays] = useState(30);
  const [inputDays, setInputDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    siteSettingsService.get()
      .then(data => {
        setDurationDays(data.listingDurationDays);
        setInputDays(data.listingDurationDays);
      })
      .catch(() => setError('Ayarlar yüklenemedi.'))
      .finally(() => setLoading(false));
  }, []);

  async function handleSave() {
    const days = parseInt(inputDays, 10);
    if (!days || days < 1 || days > 365) {
      setError('Lütfen 1-365 arasında bir değer girin.');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess(false);
    try {
      const data = await siteSettingsService.updateListingDuration(days);
      setDurationDays(data.listingDurationDays);
      setInputDays(data.listingDurationDays);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch {
      setError('Kayıt sırasında hata oluştu.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-5">
      {/* Listing Duration */}
      <div className="bg-white rounded-2xl shadow-apple p-5">
        <h3 className="font-semibold text-apple-gray-dark mb-4 flex items-center gap-2">
          <Settings className="w-4 h-4 text-apple-blue" />
          İlan Yayın Süresi
        </h3>

        {loading ? (
          <p className="text-sm text-apple-gray-mid">Yükleniyor…</p>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-apple-gray-mid">
              Yeni oluşturulan ilanların kaç gün sonra <strong>"Süresi Doldu"</strong> olarak işaretleneceğini belirler.
              Mevcut aktif değer: <strong>{durationDays} gün</strong>
            </p>

            <div className="flex items-center gap-3">
              <input
                type="number"
                min={1}
                max={365}
                value={inputDays}
                onChange={e => setInputDays(e.target.value)}
                className="input-field w-32 text-center"
                placeholder="30"
              />
              <span className="text-sm text-apple-gray-mid">gün</span>
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn-primary text-sm px-4 py-2 disabled:opacity-50"
              >
                {saving ? 'Kaydediliyor…' : 'Kaydet'}
              </button>
            </div>

            {success && (
              <p className="text-sm text-green-600 flex items-center gap-1">
                <CheckCircle className="w-4 h-4" /> Kaydedildi! Yeni ilanlar bu süreye göre oluşturulacak.
              </p>
            )}
            {error && (
              <p className="text-sm text-red-500">{error}</p>
            )}

            <p className="text-xs text-apple-gray-mid bg-apple-gray rounded-xl p-3">
              ⚠️ Bu ayar yalnızca <strong>yeni oluşturulacak</strong> ilanları etkiler. Mevcut ilanların süresi değişmez.
            </p>
          </div>
        )}
      </div>

      {/* Other static settings */}
      {[
        {
          titleKey: 'admin.settings_listing_limits',
          items: [
            [t('admin.settings_normal_member'), `5 ${t('admin.settings_listing_unit')}`],
            [t('admin.settings_vip_member'),    `20 ${t('admin.settings_listing_unit')}`],
            [t('admin.settings_shop'),          `100 ${t('admin.settings_listing_unit')}`],
          ],
        },
        {
          titleKey: 'admin.settings_featured_prices',
          items: [
            [t('admin.settings_featured_7'),         '4,99€'],
            [t('admin.settings_featured_30'),        '14,99€'],
            [t('admin.settings_featured_unlimited'), '49,99€'],
          ],
        },
        {
          titleKey: 'admin.settings_membership_prices',
          items: [
            [t('admin.settings_vip_monthly'),  '9,99€'],
            [t('admin.settings_shop_monthly'), '24,99€'],
          ],
        },
      ].map(section => (
        <div key={section.titleKey} className="bg-white rounded-2xl shadow-apple p-5">
          <h3 className="font-semibold text-apple-gray-dark mb-4">{t(section.titleKey)}</h3>
          <div className="space-y-3">
            {section.items.map(([label, value]) => (
              <div key={label} className="flex items-center justify-between">
                <span className="text-sm text-apple-gray-mid">{label}</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-apple-gray-dark">{value}</span>
                  <button className="text-xs text-apple-blue hover:underline">{t('admin.edit')}</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Local-only mock reports (no backend endpoint yet)
const INIT_REPORTS = [
  { id: 1, type: 'Sahte ilan',        listingTitle: 'iPhone 14 Pro Max...', reporter: 'Max M.',  status: 'pending'  },
  { id: 2, type: 'Uygunsuz içerik',   listingTitle: 'Sony WH-1000XM5...',  reporter: 'Anna K.', status: 'pending'  },
  { id: 3, type: 'Spam',              listingTitle: 'MacBook Pro 14"...',   reporter: 'Hans B.', status: 'resolved' },
];

export default function Admin() {
  const { user, isAdmin, isModerator } = useAuth();
  const { t } = useLanguage();
  const [activeTab, setActiveTab]   = useState('dashboard');

  const [listings, setListings]     = useState([]);
  const [users,    setUsers]        = useState([]);
  const [reports,  setReports]      = useState(INIT_REPORTS);

  const [loadingListings, setLoadingListings] = useState(false);
  const [loadingUsers,    setLoadingUsers]    = useState(false);
  const [stats,           setStats]           = useState({ totalUsers: 0, activeListings: 0, reserved: 0 });

  useEffect(() => {
    if (!user || (!isAdmin && !isModerator)) return;
    fetchListings();
    fetchUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!user || (!isAdmin && !isModerator)) return <Navigate to="/" />;

  async function fetchListings() {
    setLoadingListings(true);
    try {
      const data = await listingService.getAll({ page: 1, pageSize: 50 });
      const items = data?.items || data || [];
      setListings(items);
      setStats(prev => ({
        ...prev,
        activeListings: data?.totalCount ?? items.length,
        reserved: items.filter(l => l.isReserved).length,
      }));
    } catch { /* ignore */ }
    finally { setLoadingListings(false); }
  }

  async function fetchUsers() {
    setLoadingUsers(true);
    try {
      const data = await userService.getAll();
      const items = Array.isArray(data) ? data : [];
      setUsers(items);
      setStats(prev => ({ ...prev, totalUsers: items.length }));
    } catch { /* ignore */ }
    finally { setLoadingUsers(false); }
  }

  async function handleDeleteListing(id) {
    if (!window.confirm(t('admin.confirm_delete_listing'))) return;
    try {
      await listingService.delete(id);
      setListings(prev => prev.filter(l => l.id !== id));
    } catch { alert('Silme işlemi başarısız.'); }
  }

  const pendingReports = reports.filter(r => r.status === 'pending').length;

  const STAT_CARDS = [
    { labelKey: 'admin.total_members',   value: stats.totalUsers.toLocaleString('de-DE'), icon: Users,         color: 'text-apple-blue',   bg: 'bg-blue-50'   },
    { labelKey: 'admin.active_listings', value: stats.activeListings.toLocaleString('de-DE'), icon: Package,   color: 'text-apple-green',  bg: 'bg-green-50'  },
    { labelKey: 'admin.reserved_listings', value: stats.reserved.toLocaleString('de-DE'), icon: TrendingUp,   color: 'text-apple-orange', bg: 'bg-orange-50' },
    { labelKey: 'admin.pending_reports', value: pendingReports.toString(),             icon: AlertTriangle,   color: 'text-apple-red',    bg: 'bg-red-50'    },
  ];

  return (
    <div className="min-h-screen bg-apple-gray">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-apple-gray-dark">{t('admin.title')}</h1>
            <p className="text-sm text-apple-gray-mid">
              {t('admin.hello')}, {user.name} · {isAdmin ? t('admin.super_admin') : t('admin.moderator')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { fetchListings(); fetchUsers(); }}
              className="p-2 rounded-xl hover:bg-white transition-colors text-apple-gray-mid"
              title={t('admin.refresh')}
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <Link to="/" className="btn-secondary text-sm">{t('admin.back_to_site')}</Link>
          </div>
        </div>

        <div className="flex gap-6">
          {/* Sidebar */}
          <div className="w-52 shrink-0 hidden md:block">
            <div className="bg-white rounded-2xl shadow-apple overflow-hidden">
              {TABS.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-apple-blue text-white' : 'text-apple-gray-mid hover:bg-apple-gray hover:text-apple-gray-dark'}`}
                  >
                    <Icon className="w-4 h-4" /> {t(tab.labelKey)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Mobile tabs */}
          <div className="flex gap-2 mb-4 md:hidden overflow-x-auto w-full pb-1">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${activeTab === tab.id ? 'bg-apple-blue text-white' : 'bg-white text-apple-gray-mid'}`}
              >
                {t(tab.labelKey)}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 space-y-5">

            {/* ── Dashboard ── */}
            {activeTab === 'dashboard' && (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {STAT_CARDS.map(s => {
                    const Icon = s.icon;
                    return (
                      <div key={s.labelKey} className="bg-white rounded-2xl shadow-apple p-5">
                        <div className={`w-10 h-10 rounded-xl ${s.bg} flex items-center justify-center mb-3`}>
                          <Icon className={`w-5 h-5 ${s.color}`} />
                        </div>
                        <p className="text-2xl font-bold text-apple-gray-dark">{s.value}</p>
                        <p className="text-xs text-apple-gray-mid mt-0.5">{t(s.labelKey)}</p>
                      </div>
                    );
                  })}
                </div>

                <div className="grid lg:grid-cols-2 gap-5">
                  {/* Recent listings */}
                  <div className="bg-white rounded-2xl shadow-apple p-5">
                    <h3 className="font-semibold text-apple-gray-dark mb-4 flex items-center gap-2">
                      <Package className="w-4 h-4 text-apple-blue" /> {t('admin.recent_listings')}
                    </h3>
                    {loadingListings ? (
                      <div className="space-y-3">
                        {[...Array(4)].map((_, i) => (
                          <div key={i} className="flex items-center gap-3 animate-pulse">
                            <div className="w-10 h-10 rounded-lg bg-gray-200" />
                            <div className="flex-1 space-y-1.5">
                              <div className="h-3 bg-gray-200 rounded w-3/4" />
                              <div className="h-2.5 bg-gray-200 rounded w-1/2" />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {listings.slice(0, 4).map(l => (
                          <div key={l.id} className="flex items-center gap-3">
                            {l.images?.[0] ? (
                              <img src={l.images[0]} alt="" className="w-10 h-10 rounded-lg object-cover" />
                            ) : (
                              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-xl">📦</div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-apple-gray-dark truncate">{l.title}</p>
                              <p className="text-xs text-apple-gray-mid">{formatPrice(l.currentPrice)} · {l.city}</p>
                            </div>
                            <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${l.isReserved ? 'bg-orange-100 text-orange-600' : 'bg-green-100 text-green-700'}`}>
                              {l.isReserved ? t('admin.status_reserved') : t('admin.status_active')}
                            </span>
                          </div>
                        ))}
                        {listings.length === 0 && (
                          <p className="text-sm text-apple-gray-mid text-center py-4">{t('admin.no_listings')}</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Pending reports */}
                  <div className="bg-white rounded-2xl shadow-apple p-5">
                    <h3 className="font-semibold text-apple-gray-dark mb-4 flex items-center gap-2">
                      <Flag className="w-4 h-4 text-apple-red" /> {t('admin.pending_complaints')}
                    </h3>
                    <div className="space-y-3">
                      {reports.filter(r => r.status === 'pending').map(r => (
                        <div key={r.id} className="flex items-start gap-3 p-3 bg-red-50 rounded-xl">
                          <AlertTriangle className="w-4 h-4 text-apple-red mt-0.5 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-apple-gray-dark">{r.type}</p>
                            <p className="text-xs text-apple-gray-mid truncate">{r.listingTitle} · {r.reporter}</p>
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <button
                              onClick={() => setReports(prev => prev.map(x => x.id === r.id ? { ...x, status: 'resolved' } : x))}
                              className="p-1 rounded-lg bg-green-100 text-apple-green hover:bg-green-200 transition-colors"
                            ><CheckCircle className="w-4 h-4" /></button>
                            <button
                              onClick={() => setReports(prev => prev.map(x => x.id === r.id ? { ...x, status: 'dismissed' } : x))}
                              className="p-1 rounded-lg bg-gray-100 text-apple-gray-mid hover:bg-gray-200 transition-colors"
                            ><XCircle className="w-4 h-4" /></button>
                          </div>
                        </div>
                      ))}
                      {reports.filter(r => r.status === 'pending').length === 0 && (
                        <p className="text-sm text-apple-gray-mid text-center py-4">{t('admin.no_pending')}</p>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* ── Listings ── */}
            {activeTab === 'listings' && (
              <div className="bg-white rounded-2xl shadow-apple overflow-hidden">
                <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                  <h3 className="font-semibold text-apple-gray-dark">
                    {t('admin.listing_management')}
                    {listings.length > 0 && <span className="ml-2 text-xs font-normal text-apple-gray-mid">({listings.length})</span>}
                  </h3>
                  <button onClick={fetchListings} className="text-xs text-apple-blue hover:underline flex items-center gap-1">
                    <RefreshCw className="w-3 h-3" /> {t('admin.refresh')}
                  </button>
                </div>
                {loadingListings ? (
                  <div className="p-8 text-center text-apple-gray-mid text-sm animate-pulse">{t('admin.loading')}</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-apple-gray">
                        <tr>
                          {[t('admin.col_listing'), t('admin.col_seller'), t('admin.col_price'), t('admin.col_city'), t('admin.col_views'), t('admin.col_status'), t('admin.col_action')].map(h => (
                            <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-apple-gray-mid uppercase tracking-wide">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {listings.map(l => (
                          <tr key={l.id} className="hover:bg-apple-gray/50 transition-colors">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                {l.images?.[0]
                                  ? <img src={l.images[0]} alt="" className="w-8 h-8 rounded-lg object-cover shrink-0" />
                                  : <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-base shrink-0">📦</div>
                                }
                                <div>
                                  <span className="font-medium text-apple-gray-dark line-clamp-1 max-w-[140px]">{l.title}</span>
                                  <p className="text-[10px] text-apple-gray-mid">{l.listingNo}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-apple-gray-mid">{l.seller?.name || '—'}</td>
                            <td className="px-4 py-3 font-semibold text-apple-gray-dark">{formatPrice(l.currentPrice)}</td>
                            <td className="px-4 py-3 text-apple-gray-mid">{l.city || '—'}</td>
                            <td className="px-4 py-3 text-apple-gray-mid">{(l.viewCount || 0).toLocaleString('de-DE')}</td>
                            <td className="px-4 py-3">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${l.isReserved ? 'bg-orange-100 text-orange-600' : 'bg-green-100 text-green-700'}`}>
                                {l.isReserved ? t('admin.status_reserved') : t('admin.status_active')}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-1">
                                <Link to={`/listing/${l.id}`} className="p-1.5 rounded-lg hover:bg-apple-gray transition-colors text-apple-gray-mid">
                                  <Eye className="w-3.5 h-3.5" />
                                </Link>
                                <button
                                  onClick={() => handleDeleteListing(l.id)}
                                  className="p-1.5 rounded-lg hover:bg-red-50 transition-colors text-apple-gray-mid hover:text-apple-red"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {listings.length === 0 && (
                          <tr><td colSpan={7} className="text-center py-10 text-apple-gray-mid text-sm">İlan bulunamadı.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ── Users ── */}
            {activeTab === 'users' && (
              <div className="bg-white rounded-2xl shadow-apple overflow-hidden">
                <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                  <h3 className="font-semibold text-apple-gray-dark">
                    {t('admin.user_management')}
                    {users.length > 0 && <span className="ml-2 text-xs font-normal text-apple-gray-mid">({users.length} {t('admin.members_count')})</span>}
                  </h3>
                  <button onClick={fetchUsers} className="text-xs text-apple-blue hover:underline flex items-center gap-1">
                    <RefreshCw className="w-3 h-3" /> {t('admin.refresh')}
                  </button>
                </div>
                {loadingUsers ? (
                  <div className="p-8 text-center text-apple-gray-mid text-sm animate-pulse">{t('admin.loading')}</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-apple-gray">
                        <tr>
                          {[t('admin.col_member'), t('admin.col_role'), t('admin.col_city'), t('admin.col_sales'), t('admin.col_rating'), t('admin.col_action')].map(h => (
                            <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-apple-gray-mid uppercase tracking-wide">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {users.map(u => {
                          const rb = getRoleBadgeInfo(u.role);
                          const initial = (u.userNameSurname || u.name || '?')[0].toUpperCase();
                          return (
                            <tr key={u.id} className="hover:bg-apple-gray/50 transition-colors">
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  {u.userPhoto
                                    ? <img src={u.userPhoto} alt="" className="w-8 h-8 rounded-full object-cover" />
                                    : <div className="w-8 h-8 rounded-full bg-apple-blue flex items-center justify-center text-white text-xs font-bold">{initial}</div>
                                  }
                                  <div>
                                    <p className="font-medium text-apple-gray-dark">{u.userNameSurname || u.name}</p>
                                    {u.isVerified && <span className="text-[10px] text-apple-green">✓ Onaylı</span>}
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${rb.className}`}>{t(rb.labelKey)}</span>
                              </td>
                              <td className="px-4 py-3 text-apple-gray-mid">{u.city || '—'}</td>
                              <td className="px-4 py-3 text-apple-gray-mid">{u.totalSales ?? 0}</td>
                              <td className="px-4 py-3">
                                <span className="flex items-center gap-1 text-apple-gray-dark">
                                  <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                                  {(u.rating ?? 0).toFixed(1)}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex gap-1">
                                  <Link to={`/profile/${u.id}`} className="p-1.5 rounded-lg hover:bg-apple-gray transition-colors text-apple-gray-mid">
                                    <Eye className="w-3.5 h-3.5" />
                                  </Link>
                                  {isAdmin && (
                                    <button
                                      title="Rol değiştir (yakında)"
                                      className="p-1.5 rounded-lg hover:bg-orange-50 transition-colors text-apple-gray-mid hover:text-apple-orange"
                                    >
                                      <Shield className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        {users.length === 0 && (
                          <tr><td colSpan={6} className="text-center py-10 text-apple-gray-mid text-sm">Üye bulunamadı.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ── Reports ── */}
            {activeTab === 'reports' && (
              <div className="space-y-4">
                {reports.map(r => (
                  <div key={r.id} className={`bg-white rounded-2xl shadow-apple p-5 border-l-4 ${r.status === 'pending' ? 'border-apple-red' : r.status === 'resolved' ? 'border-apple-green' : 'border-gray-300'}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${r.status === 'pending' ? 'bg-red-100 text-apple-red' : r.status === 'resolved' ? 'bg-green-100 text-apple-green' : 'bg-gray-100 text-gray-500'}`}>
                          {r.status === 'pending' ? t('admin.report_pending') : r.status === 'resolved' ? t('admin.report_resolved') : t('admin.report_dismissed')}
                        </span>
                        <h4 className="font-semibold text-apple-gray-dark mt-2">{r.type}</h4>
                        <p className="text-sm text-apple-gray-mid">İlan: {r.listingTitle}</p>
                        <p className="text-xs text-apple-gray-mid mt-1">Şikayet eden: {r.reporter}</p>
                      </div>
                      {r.status === 'pending' && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => setReports(prev => prev.map(x => x.id === r.id ? { ...x, status: 'resolved' } : x))}
                            className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1"
                          ><CheckCircle className="w-3 h-3" /> Çöz</button>
                          <button
                            onClick={() => setReports(prev => prev.map(x => x.id === r.id ? { ...x, status: 'dismissed' } : x))}
                            className="btn-secondary text-xs px-3 py-1.5 flex items-center gap-1"
                          ><XCircle className="w-3 h-3" /> Reddet</button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ── Ads ── */}
            {activeTab === 'ads' && <AdsTab />}

            {/* ── Settings ── */}
            {activeTab === 'settings' && <SettingsTab />}

          </div>
        </div>
      </div>
    </div>
  );
}
